import { useState } from 'react';
import {
	ChevronLeft,
	ChevronRight,
	HelpCircle,
	MessageSquare,
	Minus,
	Plus,
} from 'lucide-react';
import { WHATSAPP_LINK_FORM } from '@/constants/whatsapp';
import { useGetQuestions } from '@/services/queries/questions';

const ITEMS_PER_PAGE = 5;

export function NewLandingFaqSection() {
	const { data: questionsData } = useGetQuestions();
	const [openId, setOpenId] = useState<string | null>(null);
	const [currentPage, setCurrentPage] = useState(0);

	const questions = questionsData ?? [];
	const totalPages = Math.ceil(questions.length / ITEMS_PER_PAGE);
	const startIndex = currentPage * ITEMS_PER_PAGE;
	const visibleQuestions = [...questions]
		.sort((a, b) => a.priority - b.priority)
		.slice(startIndex, startIndex + ITEMS_PER_PAGE);

	const toggleQuestion = (id: string) => {
		setOpenId((prev) => (prev === id ? null : id));
	};

	const nextPage = () =>
		setCurrentPage((prev) => (prev === totalPages - 1 ? 0 : prev + 1));
	const prevPage = () =>
		setCurrentPage((prev) => (prev === 0 ? totalPages - 1 : prev - 1));
	const goToPage = (page: number) => setCurrentPage(page);

	return (
		<section
			id="central-ajuda"
			className="border-b border-brand-orange/10 bg-[#fffcfb] py-24"
		>
			<div className="mx-auto max-w-3xl px-6 text-center">
				<h2 className="mb-12 text-3xl font-bold text-[#3d3431]">
					Ficou com alguma dúvida?
				</h2>

				{questionsData && (
					<>
						<div className="space-y-4 text-left">
							{visibleQuestions.map((item) => {
								const isExpanded = openId === item.id;
								return (
									<div
										key={item.id}
										className={`overflow-hidden rounded-2xl border bg-white transition-all duration-300 ${
											isExpanded
												? 'border-brand-orange/30 shadow-md shadow-brand-orange/5'
												: 'border-brand-orange/10 hover:border-brand-orange/20'
										}`}
									>
										<button
											type="button"
											onClick={() =>
												toggleQuestion(item.id)
											}
											className="flex w-full cursor-pointer items-center justify-between gap-4 px-6 py-5 text-left font-bold text-[#3d3431] focus:outline-none"
											aria-expanded={isExpanded}
										>
											<span className="flex items-center gap-2.5 text-sm md:text-base">
												<HelpCircle
													className={`h-4 w-4 shrink-0 transition-colors ${
														isExpanded
															? 'text-brand-orange'
															: 'text-gray-400'
													}`}
												/>
												{item.question}
											</span>
											<span
												className={`shrink-0 rounded-lg p-1.5 transition-colors ${
													isExpanded
														? 'bg-brand-orange/10 text-brand-orange'
														: 'bg-gray-100 text-gray-400'
												}`}
											>
												{isExpanded ? (
													<Minus className="h-4 w-4" />
												) : (
													<Plus className="h-4 w-4" />
												)}
											</span>
										</button>

										<div
											className={`overflow-hidden transition-all duration-300 ease-in-out ${
												isExpanded
													? 'max-h-[500px] border-t border-brand-orange/5 px-6 py-4 opacity-100'
													: 'max-h-0 opacity-0'
											}`}
										>
											<p className="text-xs font-light leading-relaxed text-gray-500 md:text-sm">
												{item.answer}
											</p>
										</div>
									</div>
								);
							})}
						</div>

						{questions.length > ITEMS_PER_PAGE && (
							<div className="flex items-center justify-center gap-6 pt-8">
								<button
									type="button"
									onClick={prevPage}
									className="cursor-pointer rounded-full p-2 text-[#3d3431] transition-colors hover:bg-brand-orange/10"
									aria-label="Página anterior"
								>
									<ChevronLeft className="h-5 w-5" />
								</button>
								<div className="flex justify-center space-x-2.5">
									{Array.from({ length: totalPages }).map(
										(_, index) => (
											<button
												key={index}
												type="button"
												onClick={() => goToPage(index)}
												className={`h-2 cursor-pointer rounded-full transition-all duration-300 ${
													currentPage === index
														? 'w-6 bg-brand-orange'
														: 'w-2 bg-brand-orange/25 hover:bg-brand-orange/40'
												}`}
												aria-label={`Ir para página ${
													index + 1
												}`}
											/>
										),
									)}
								</div>
								<button
									type="button"
									onClick={nextPage}
									className="cursor-pointer rounded-full p-2 text-[#3d3431] transition-colors hover:bg-brand-orange/10"
									aria-label="Próxima página"
								>
									<ChevronRight className="h-5 w-5" />
								</button>
							</div>
						)}
					</>
				)}

				<div className="mx-auto mt-12 flex max-w-2xl flex-col items-center justify-between gap-4 rounded-2xl border border-brand-orange/10 bg-brand-orange/5 p-6 text-left md:flex-row">
					<div className="flex items-center gap-3">
						<div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-brand-orange/15 text-brand-orange">
							<MessageSquare className="h-5 w-5" />
						</div>
						<div>
							<h4 className="text-sm font-bold text-[#3d3431]">
								Ainda precisa de esclarecimento?
							</h4>
							<p className="text-xs leading-tight text-gray-500">
								Nosso atendimento focado e acolhedor está de
								prontidão de segunda a sexta.
							</p>
						</div>
					</div>
					<a
						href={WHATSAPP_LINK_FORM}
						target="_blank"
						rel="noopener noreferrer"
						className="shrink-0 cursor-pointer rounded-lg bg-[#3d3431] px-5 py-2.5 text-center text-xs font-bold text-white transition-colors hover:bg-black"
					>
						Falar pelo WhatsApp
					</a>
				</div>
			</div>
		</section>
	);
}
